import React, { useMemo } from 'react';
import { Popover, Text, Title, useClassnames } from '@wb-tech/ui-react';
import { useTranslation } from 'react-i18next';

import { formatNumber } from '#helper/format/number';
import { PORTAL_LINK } from '#core/const';
import type { IGetApiV5SupplierApiResponse } from '#adapter/api/campaigns/v5/supplier/types';
import type { IModelBalanceResponse } from '#adapter/api/campaigns/v3/balance/types';
import { useCountryCode } from '#hook/use-country-code';

import { WhatIsBalanceContent } from '#component/header/components/what-is-balance-content';
import { ChineseBonuses } from '#component/chinese-bonuses';
import { LanguageSelect } from '#component/language-select';
import style from '#component/header/index.module.pcss';

interface IUserInfoContent {
    balanceData?: IModelBalanceResponse,
    supplierData?: IGetApiV5SupplierApiResponse,
    balanceSkipped: boolean
}

export const UserInfoContent = ({ balanceData, balanceSkipped, supplierData }: IUserInfoContent) => {
    const cn = useClassnames(style);
    const { t } = useTranslation();

    const { isChinese, isRussian } = useCountryCode();

    const balance = useMemo(() => {
        const valueBySupplier = supplierData?.contract?.reduce((acc, curr) => {
            let newAcc = acc;

            if(curr.active && curr.balance) {
                newAcc += curr.balance;
            }

            return newAcc;
        }, 0);

        const valueByBalance = balanceData?.content?.balance ?? 0;

        if(balanceSkipped) {
            return valueBySupplier;
        }

        return valueByBalance;
    }, [balanceData?.content?.balance, balanceSkipped, supplierData]);

    if(!supplierData) {
        return null;
    }

    return (
        <div className={cn('header__dropdown')}>
            <div className={cn('header__dropdown-profile')}>
                <Title tagName="h4">{t('components.header.dropdown.profile')}</Title>
            </div>
            <div className={cn('header__dropdown-content')}>
                <div className={cn('header__dropdown-content-item')}>
                    {supplierData.name}
                    <Text
                        presetColor="secondary"
                        presetSize="caption"
                    >
                        {t('components.header.dropdown.content.company.inn', {
                            value: supplierData.inn
                        })}
                    </Text>
                </div>
                {(balance || balance === 0) && (
                    <div className={cn('header__dropdown-content-item')}>
                        <Text
                            presetColor="secondary"
                            presetSize="caption"
                        >
                            {t('components.header.dropdown.content.balance.title')}
                        </Text>
                        <Text>
                            {t('components.header.dropdown.content.balance.value', {
                                value: formatNumber(balance)
                            })}
                        </Text>
                    </div>
                )}
                {isRussian && !!balanceData?.content?.net && (
                    <div className={cn('header__dropdown-content-item')}>
                        <Text
                            presetColor="secondary"
                            presetSize="caption"
                        >
                            {t('components.header.dropdown.content.net.title')}
                        </Text>
                        <div className={cn('header__dropdown-content-item_balance')}>
                            <Text>
                                {t('components.header.dropdown.content.balance.value', {
                                    value: formatNumber(balanceData?.content?.net)
                                })}
                            </Text>
                            <WhatIsBalanceContent />
                        </div>
                    </div>
                )}
                {!!balanceData?.content?.bonus && (
                    <div className={cn('header__dropdown-content-item')}>
                        <Text
                            presetColor="secondary"
                            presetSize="caption"
                        >
                            {t('components.header.dropdown.content.bonus.title')}
                        </Text>
                        <Text>
                            {t('components.header.dropdown.content.bonus.value', {
                                value: formatNumber(balanceData?.content?.bonus)
                            })}
                        </Text>
                    </div>
                )}
                {isChinese && balanceData?.content?.bonus && <ChineseBonuses />}

                {!isChinese && !!balanceData?.content?.cashback && (
                    <div className={cn('header__dropdown-content-item')}>
                        <Text
                            presetColor="secondary"
                            presetSize="caption"
                        >
                            {t('components.header.dropdown.content.cashback.title')}
                        </Text>
                        <Text>
                            {t('components.header.dropdown.content.cashback.value', {
                                value: formatNumber(balanceData?.content?.cashback)
                            })}
                        </Text>
                    </div>
                )}
            </div>
            <div className={cn('header__dropdown-change-company')}>
                <Popover
                    className={{
                        'popover__trigger': cn('header__dropdown-change-company-trigger'),
                        'popover__content': cn('header__dropdown-change-company-content')
                    }}
                    render={(
                        <Text
                            presetColor="inverse"
                            presetSize="caption"
                        >
                            {t('components.header.dropdown.change-company.popover')}
                        </Text>
                    )}
                    trigger="hover"
                    triggerOffset={4}
                >
                    <a
                        href={PORTAL_LINK}
                        className={cn('header__dropdown-change-company-link')}
                    >
                        <Text>{t('components.header.dropdown.change-company.text')}</Text>
                    </a>
                </Popover>
            </div>

            <div className={cn('header__language-select')}>
                <LanguageSelect />
            </div>

            <div className={cn('header__dropdown-out')}>
                <a
                    href={PORTAL_LINK}
                    className={cn('header__dropdown-out-link')}
                >
                    <Text presetColor="secondary">{t('components.header.dropdown.out')}</Text>
                </a>
            </div>
        </div>
    );
};
